import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { NgbModal, NgbModalRef } from '@cmps/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';
import { AuthService } from '../auth/auth.service';
import { CmpsDatePipe } from '../common/date.pipe';
import { ModalWebFormComponent } from '../webform/modal-webform.component';
import { WidgetConfiguration } from '../widget/config.widget.model';
import { ModalWidgetComponent } from '../widget/modal.widget.component';
import { CMPS_WIDGET_CONFIG } from '../widget/widget.metadata';
import { WidgetService } from '../widget/widget.service';
import {
    createWebFormsScorecardUrl,
    generateScorecardModalHeader,
} from '../widget/widget.util';

import { CaoOpportunityWidgetComponent } from './cao-opportunity.widget.component';
import { TranslationKeyMap } from './legacy-widget.i18n';
import { LegacyWidgetService } from './legacy-widget.service';
import { ManagementEffectiveness } from './management-effectiveness.widget.model';
import { Mi2DetailsWidgetComponent } from './mi2-details.widget.component';
import {
    Mi2DetailsPopupItem,
    Mi2DetailsPopupItemScorecard,
} from './mi2-details.widget.model';

@Component({
    selector: 'cmps-widget-management-effectiveness',
    templateUrl: './management-effectiveness.widget.component.html',
})
export class ManagementEffectivenessWidgetComponent
    implements OnInit, OnDestroy
{
    i18n = TranslationKeyMap;
    hasPendingData: boolean;
    data: ManagementEffectiveness;
    private _modal: NgbModalRef;

    constructor(
        private readonly _modalService: NgbModal,
        private readonly _translateService: TranslateService,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
        @Inject(CMPS_WIDGET_CONFIG) public config: WidgetConfiguration,
        private readonly _widgetService: WidgetService,
        private readonly _service: LegacyWidgetService,
        private readonly _authService: AuthService,
        private readonly _datePipe: CmpsDatePipe,
    ) {}

    ngOnInit(): void {
        this.hasPendingData = true;
        this._service
            .managementEffectiveness()
            // eslint-disable-next-line no-return-assign
            .subscribe(
                (data) => (this.data = data),
                // eslint-disable-next-line n/handle-callback-err, no-return-assign
                (error) => (this.hasPendingData = false),
                // eslint-disable-next-line no-return-assign
                () => (this.hasPendingData = false),
            );
    }

    ngOnDestroy(): void {
        if (this._modal) {
            this._modal.close();
        }
    }

    showDetails(): void {
        this._modal = this._modalService.open(ModalWidgetComponent, {
            size: 'lg',
        });
        this._modal.componentInstance.config = {
            header: this.config.title,
            component: CaoOpportunityWidgetComponent,
            options: {
                userId: this._authService.user.id,
            },
        };
        // eslint-disable-next-line no-return-assign
        this._modal.result.then(
            (result) => (this._modal = null),
            (reason) => (this._modal = null),
        );
    }

    showMi2Details(): void {
        this._modal = this._modalService.open(ModalWidgetComponent, {
            size: 'lg',
        });
        this._modal.componentInstance.config = {
            header: this.config.title,
            component: Mi2DetailsWidgetComponent,
        };
        this._modal.result.then(
            (result) => {
                this._modal = null;
                if (typeof result === 'object') {
                    this._showScorecard(result.item, result.scorecard);
                }
                // eslint-disable-next-line no-return-assign
            },
            (reason) => (this._modal = null),
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private _showScorecard(
        item: Mi2DetailsPopupItem,
        scorecard: Mi2DetailsPopupItemScorecard,
    ) {
        const header = generateScorecardModalHeader(
            this._translateService,
            this._datePipe,
            item.username,
            scorecard.startDate,
            scorecard.endDate,
        );
        const url = `${
            this._appConfig.WEB_FORMS_BASE_URL
        }${createWebFormsScorecardUrl(this._datePipe, {
            id: scorecard.id,
            userId: item.userId,
            username: item.username,
            startDate: scorecard.startDate,
            endDate: scorecard.endDate,
            periodTypeId: this._widgetService.dateRange.value.type,
        })}`;

        this._modal = this._modalService.open(ModalWebFormComponent, {
            windowClass: 'cmps-iframe-scorecard',
        });
        this._modal.componentInstance.config = {
            header,
            currentUrl: url,
        };
        this._modal.componentInstance.hasScrolling = false;
        // eslint-disable-next-line no-return-assign
        this._modal.result.then(
            (result) => (this._modal = null),
            (reason) => (this._modal = null),
        );
    }
}
